// Error Boundary to catch rendering errors
import React, { Component, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from './ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Check if it's a chunk load error
    const isChunkError = 
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk') ||
      (error as any).type === 'chunk-load-error';
    
    if (isChunkError) {
      console.error('❌ Chunk load error detected in ErrorBoundary:', error);
      // Don't send chunk errors to Sentry - they're usually deployment-related
      return;
    }
    
    // Send to Sentry if available
    import('../utils/sentry').then(({ captureException }) => {
      captureException(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      });
    }).catch(() => {
      // Sentry not available, continue without it
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Check if it's a chunk load error
      const isChunkError = 
        this.state.error?.message?.includes('Failed to fetch dynamically imported module') ||
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('Loading CSS chunk') ||
        (this.state.error as any)?.type === 'chunk-load-error';

      // Default full-page error
      return (
        <div className="min-h-screen bg-dark flex items-center justify-center p-4 sm:p-6">
          <div className="text-center w-full max-w-md">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-red-500/20 rounded-full mb-4 sm:mb-6">
              <AlertCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-400" />
            </div>
            <h1 className="text-xl sm:text-2xl font-dela text-white mb-3 sm:mb-4 px-2">
              {isChunkError ? 'Failed to Load Page' : 'Something went wrong'}
            </h1>
            <p className="text-sm sm:text-base text-white/60 font-inter mb-5 sm:mb-6 px-2 break-words">
              {isChunkError 
                ? 'A required file failed to load. This usually happens after a deployment. Please reload the page to get the latest version.'
                : (this.state.error?.message || 'An unexpected error occurred')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 justify-center items-stretch sm:items-center">
              <Button
                onClick={() => {
                  // Clear cache before reload for chunk errors
                  if (isChunkError && 'caches' in window) {
                    caches.keys().then(names => {
                      names.forEach(name => caches.delete(name));
                      window.location.reload();
                    });
                  } else {
                    window.location.reload();
                  }
                }}
                variant="primary"
                className="w-full sm:w-auto min-h-[44px] touch-manipulation"
              >
                {isChunkError ? 'Reload & Clear Cache' : 'Reload Page'}
              </Button>
              {!isChunkError && (
                <Button
                  onClick={this.handleReset}
                  variant="secondary"
                  className="w-full sm:w-auto min-h-[44px] touch-manipulation"
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}









